import { Link } from "@tanstack/react-router";
import { SectionMark } from "@/components/section-mark";
import { AmbientVideo } from "@/components/ambient-video";
import { Reveal } from "@/components/reveal";
import { SpeakerTestimonialCard } from "@/components/speaker-testimonial-card";
import { TestimonialsTape } from "@/components/testimonials-tape";
import { splitEvents } from "@/content/site-content";
import { speakerTestimonials, speakingTopics } from "@/content/speaking";
import { cta } from "@/lib/analytics";
import { logoSize } from "@/lib/logo-size";
import mmShowreel from "@/assets/dzien-dobry-video.mp4.asset.json";
import mmShowreelPoster from "@/assets/dzien-dobry-poster.jpg.asset.json";
import mmShowreelPosterWebp from "@/assets/dzien-dobry-poster.webp.asset.json";

const stages =
  "Występowałam m.in. na: Polski Kongres Przedsiębiorczości · Akademia Leona Koźmińskiego · Midel Camp · Konferencja Analiza IT · TransportCamp · Diamenty Europy";

function shortDate(date: string) {
  return date
    .replace(" września ", ".09.")
    .replace(" października ", ".10.")
    .replace(" listopada ", ".11.")
    .replace(" grudnia ", ".12.")
    .replace(" stycznia ", ".01.")
    .replace(/^(\d+)\.(\d{2})\.(\d{4})$/, "$1.$2.$3");
}

function homeTitle(title: string) {
  if (title.startsWith("Wolni od stresu")) return 'Akademia Zwierciadła "Wolni od stresu"';
  if (title === "Global Slow Medicine Conference 2026") return "Global Slow Medicine Conference";
  return title;
}

export function EventsSection() {
  const upcoming = splitEvents().upcoming.slice(0, 3);

  return (
    <section id="wystapienia" className="content-visibility-auto scroll-mt-24 border-b border-ink/10">
      <div className="container-page section-pad">
        <SectionMark number="03" label="Wystąpienia i współpraca" />

        <Reveal className="mt-7 grid items-center gap-8 md:grid-cols-2 md:gap-10 lg:gap-16">
          <div>
            <h2 className="display-xl text-[1.9rem] md:text-[2.6rem]">
              Mówię o tym, co dzieje się z człowiekiem, kiedy od jego decyzji naprawdę dużo{" "}
              <span className="serif-display italic text-coral-text">zależy</span>.
            </h2>
            <div className="mt-8 h-px w-full bg-steel" />
            <p className="lead-serif measure-text mt-8">
              O presji, odpowiedzialności, decyzjach podejmowanych bez pewności oraz o tym, jak
              sukces wpływa na człowieka i relacje.
            </p>
            <p className="mt-6 text-sm leading-relaxed text-ink-soft">
              Keynote · prelekcja · panel ekspercki · spotkanie dla zarządu
            </p>
          </div>
          <AmbientVideo
            src={mmShowreel.url}
            poster={mmShowreelPoster.url}
            posterWebp={mmShowreelPosterWebp.url}
            alt="Sylwia Sitkowska, w studiu programu „Dzień dobry”"
            mediaClassName="aspect-[16/9] w-full object-cover"
            width={1280}
            height={720}
          />
        </Reveal>

        <Reveal className="mt-10 border-t border-steel pt-8">
          <p className="eyebrow">Tematy wystąpień</p>
          <ul className="mt-6 grid gap-px border border-steel bg-steel lg:grid-cols-4">
            {speakingTopics.map((topic) => (
              <li key={topic.title} className="bg-background p-5 md:p-6">
                <h3 className="text-base font-semibold leading-snug text-ink">{topic.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/80">{topic.description}</p>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm leading-relaxed text-ink-soft">{stages}</p>
        </Reveal>

        {speakerTestimonials.length > 0 && (
          <Reveal className="mt-10 border-t border-steel pt-8">
            <p className="eyebrow">Z perspektywy organizatorów</p>
            <div className="mt-2">
              <TestimonialsTape
                id="rekomendacje-konferencji-lista"
                label="Rekomendacje organizatorów konferencji"
                items={speakerTestimonials}
                itemKey={(item) => item.name}
                renderItem={(item) => <SpeakerTestimonialCard item={item} />}
                itemClassName="w-[19rem] sm:w-[21rem] lg:w-[24rem]"
                gridClassName="xl:grid-cols-3"
                alignHeaders
              />
            </div>
          </Reveal>
        )}

        <Reveal className="mt-10 border-t border-steel pt-8">
          <p className="eyebrow">Najbliższe wystąpienia</p>
          <ul className="mt-5 divide-y divide-steel border-y border-steel">
            {upcoming.map((event) => (
              <li key={event.id}>
                <Link
                  to="/wystapienia"
                  hash={event.id}
                  className="group flex min-h-11 items-center gap-4 py-4 text-base text-ink transition-colors hover:text-coral-text sm:gap-6"
                >
                  <span className="flex h-12 w-20 flex-shrink-0 items-center justify-start sm:w-24">
                    {event.logo && (
                      <img
                        src={event.logo}
                        alt={`Logo wydarzenia ${event.title}`}
                        {...logoSize(event.logo)}
                        loading="lazy"
                        className="max-h-12 max-w-20 object-contain object-left sm:max-w-24"
                      />
                    )}
                  </span>
                  <span className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                    <span className="font-medium">{shortDate(event.date)}</span>
                    <span aria-hidden="true" className="hidden text-coral-text sm:inline">·</span>
                    <span>{homeTitle(event.title)}</span>
                    <span aria-hidden="true" className="hidden text-coral-text sm:inline">·</span>
                    <span className="text-ink-soft group-hover:text-coral-text">{event.city}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Link
            to="/kontakt"
            {...cta("zapros-mnie-na-wydarzenie", "wystapienia", "prelekcja")}
            className="btn-base btn-primary inline-flex justify-center"
          >
            Zaproś mnie na wydarzenie
          </Link>
          <Link
            to="/wystapienia"
            {...cta("wszystkie-wystapienia", "wystapienia", "prelekcja")}
            className="inline-flex min-h-11 items-center text-base font-medium text-ink underline underline-offset-4 transition-colors hover:text-coral-text"
          >
            Wszystkie wystąpienia →
          </Link>
        </Reveal>
      </div>
    </section>
  );
}